import { useTranslation } from "react-i18next";
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";

interface DoubleDownButtonProps {
  image: boolean;
  className?: string;
  canDoubleDown?: boolean;
  /** The org turned Post-Flight off; distinct from a missing permission. */
  isTypeDisabled?: boolean;
  onClick: () => void;
}

/** Image ads stay visible and cannot start a double down. */
export function DoubleDownButton({
  image,
  className,
  canDoubleDown = true,
  isTypeDisabled = false,
  onClick,
}: DoubleDownButtonProps) {
  const { t } = useTranslation();
  const label = t("postFlight.doubleDown.open", "Double down");
  if (canDoubleDown && !isTypeDisabled && !image) {
    return (
      <Button type="button" variant="ai" className={className} iconStart="sparkles" onClick={onClick}>
        {label}
      </Button>
    );
  }
  const reason = !canDoubleDown
    ? t(
        "postFlight.doubleDown.noPermission",
        "You don't have this permission for this organization. Ask a Nexus admin or customer manager.",
      )
    : isTypeDisabled
      ? t(
          "postFlight.doubleDown.typeDisabled",
          "Post-Flight is turned off for your organization. Ask a Nexus admin or customer manager to turn it on.",
        )
      : t("postFlight.doubleDown.imageBlocked", "Double Down isn't available for image ads.");
  const triggerClass = ["inline-flex cursor-not-allowed [&>button]:pointer-events-none", className]
    .filter(Boolean)
    .join(" ");
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Disabled buttons fire no pointer events, so the span keeps the tooltip and cursor. */}
          <span tabIndex={0} className={triggerClass}>
            <Button type="button" variant="ai" className="w-full" iconStart="sparkles" disabled>
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
